import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { jsPDF } from "jspdf";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";
import { useNavigate } from "react-router-dom";
import "./InvoiceGenerator.css";
import "../App.css"; // Import App.css for shared navbar and footer styles

const InvoiceGenerator = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: 1,
    billDate: "", // Initialize as empty for user-defined date
    businessName: "",
    businessAddress: "",
    phoneNumber: "",
    gstNumber: "",
    customerName: "",
    customerPhone: "",
    items: [{ description: "", productCode: "", quantity: 1, price: 0 }],
    discount: 0,
    taxRate: 0,
  });
  const [errors, setErrors] = useState({});
  const [showContactForm, setShowContactForm] = useState(false);
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [showAiPanel, setShowAiPanel] = useState(true);
  const invoiceRef = useRef(null);
  const contactRef = useRef(null);
  const navigate = useNavigate();

  // AI-powered product suggestions
  const commonProducts = [
    { name: "Consulting Services", code: "SRV001", price: 5000 },
    { name: "Web Development", code: "WEB001", price: 15000 },
    { name: "Graphic Design", code: "DES001", price: 3000 },
    { name: "Marketing Services", code: "MKT001", price: 8000 },
    { name: "Technical Support", code: "SUP001", price: 2000 },
    { name: "Software License", code: "LIC001", price: 10000 },
    { name: "Training Session", code: "TRN001", price: 4000 },
    { name: "Maintenance Contract", code: "MNT001", price: 6000 },
  ];

  // AI Smart Tax Calculator
  const aiCalculateTax = () => {
    const subtotal = calculateSubtotal();
    if (subtotal > 0 && invoiceData.taxRate === 0) {
      // Auto-suggest 18% GST for services
      if (invoiceData.items.some(item => 
        item.description.toLowerCase().includes('service') || 
        item.description.toLowerCase().includes('consultation')
      )) {
        return 18;
      }
      // Auto-suggest 12% for general goods
      return 12;
    }
    return invoiceData.taxRate;
  };

  // AI Auto-fill business type detection
  const aiDetectBusinessType = (name) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('tech') || lowerName.includes('software') || lowerName.includes('digital')) {
      setAiSuggestions(['Technology/IT Services', 'Consider 18% GST', 'Add GST number']);
    } else if (lowerName.includes('restaurant') || lowerName.includes('cafe') || lowerName.includes('food')) {
      setAiSuggestions(['Food & Beverage', 'Consider 5% GST', 'Add FSSAI number']);
    } else if (lowerName.includes('retail') || lowerName.includes('store') || lowerName.includes('shop')) {
      setAiSuggestions(['Retail Business', 'Consider 12% GST', 'Track inventory']);
    }
  };

  const metaDescription =
    "Create professional PDF invoices FREE with Dhandha Karle's Invoice Generator. GST support, unlimited invoices, WhatsApp sharing. Best free invoice maker for Indian businesses, restaurants, retail stores. Generate invoices instantly!";
  const pageTitle = "Free Invoice Generator Online | Create PDF Invoices with GST | Dhandha Karle";

  // Form toggle functions
  const toggleContactForm = () => {
    setShowContactForm(!showContactForm);
    if (!showContactForm) {
      setTimeout(() => {
        contactRef.current.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
    setIsNavOpen(false);
  };

  const toggleDemoForm = () => {
    setShowDemoForm(!showDemoForm);
    if (!showDemoForm) {
      setTimeout(() => {
        contactRef.current.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
    setIsNavOpen(false);
  };

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const goToHome = () => {
    navigate("/");
    setIsNavOpen(false);
  };

  // Email sending function
  const sendEmail = (e, formType) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);

    emailjs
      .send(
        "service_qxfzblh",
        "template_543gl6n",
        data,
        "55rDJxZlYVITGi3zR"
      )
      .then(
        (result) => {
          alert(`${formType} submitted successfully!`);
          e.target.reset();
          if (formType === "Contact") setShowContactForm(false);
          if (formType === "Demo") setShowDemoForm(false);
        },
        (error) => {
          alert("Failed to send the form. Please try again.");
          console.log(error.text);
        }
      );
  };

  // Validation logic
  const validateField = (name, value) => {
    let error = "";
    if (name === "phoneNumber" || name === "customerPhone") {
      if (!/^\d{10}$/.test(value)) error = "Must be a 10-digit number";
    }
    if (name === "businessName" && !value) error = "Business Name is required";
    if (name === "billDate" && !value) error = "Invoice Date is required";
    if (name === "invoiceNumber" && (!value || value <= 0))
      error = "Invoice Number must be greater than 0";
    if (name === "items") {
      const invalidItem = invoiceData.items.find(
        (item) => !item.description || item.price <= 0
      );
      if (invalidItem) {
        if (!invalidItem.description) error = "Item description is required";
        else if (invalidItem.price <= 0)
          error = "Item price must be greater than 0";
      }
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return !error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const parsedValue =
      name === "discount" || name === "taxRate" || name === "invoiceNumber"
        ? parseFloat(value) || 0
        : value;
    setInvoiceData((prev) => ({ ...prev, [name]: parsedValue }));
    validateField(name, parsedValue);
    
    // AI: Detect business type when business name is entered
    if (name === "businessName" && value.length > 3) {
      aiDetectBusinessType(value);
    }
  };

  // AI: Quick add product from suggestions
  const addAiProduct = (product) => {
    const newItem = {
      description: product.name,
      productCode: product.code,
      quantity: 1,
      price: product.price
    };
    setInvoiceData((prev) => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  // AI: Auto-apply smart tax rate
  const applySmartTax = () => {
    const smartRate = aiCalculateTax();
    setInvoiceData((prev) => ({ ...prev, taxRate: smartRate }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...invoiceData.items];
    updatedItems[index][field] =
      field === "quantity" || field === "price" ? parseFloat(value) || 0 : value;
    setInvoiceData((prev) => ({ ...prev, items: updatedItems }));
    validateField("items");
  };

  const addItem = () => {
    setInvoiceData((prev) => ({
      ...prev,
      items: [...prev.items, { description: "", productCode: "", quantity: 1, price: 0 }],
    }));
  };

  const removeItem = (index) => {
    setInvoiceData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateSubtotal = () => {
    return invoiceData.items.reduce(
      (sum, item) => sum + item.quantity * item.price,
      0
    );
  };

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    return (subtotal * invoiceData.taxRate) / 100;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax();
    return subtotal - invoiceData.discount + tax;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const generatePDF = () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Phenomenal gradient header background
    doc.setFillColor(14, 165, 233);
    doc.rect(0, 0, 210, 50, "F");
    
    // Add stunning decorative accent stripe
    doc.setFillColor(245, 158, 11);
    doc.rect(0, 0, 210, 5, "F");
    
    // Elegant Dhandha Karle logo/branding section
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(15, 10, 90, 18, 3, 3, "F");
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(14, 165, 233);
    doc.text("DHANDHA KARLE", 20, 18);
    
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "normal");
    doc.text("Your Business Growth Partner", 20, 23);
    
    // Main INVOICE text
    doc.setFont("helvetica", "bold");
    doc.setFontSize(36);
    doc.setTextColor(255, 255, 255);
    doc.text("INVOICE", 20, 40);
    
    // Invoice number with elegant styling (positioned below INVOICE)
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(`#${invoiceData.invoiceNumber}`, 20, 47);
    
    // Date badge in corner
    if (invoiceData.billDate) {
      doc.setFillColor(245, 158, 11);
      doc.roundedRect(140, 12, 55, 20, 3, 3, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.text("DATE", 167.5, 18, { align: "center" });
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(invoiceData.billDate), 167.5, 27, { align: "center" });
    }

    // Elegant info boxes with borders
    // Left box (From)
    doc.setDrawColor(14, 165, 233);
    doc.setLineWidth(0.5);
    doc.setFillColor(240, 249, 255);
    doc.roundedRect(15, 58, 85, 35, 2, 2, "FD");
    
    doc.setFontSize(9);
    doc.setTextColor(14, 165, 233);
    doc.setFont("helvetica", "bold");
    doc.text("FROM", 20, 63);
    
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.setFont("helvetica", "bold");
    doc.text(invoiceData.businessName || "Your Business", 20, 70);
    
    let yOffsetLeft = 76;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(71, 85, 105);
    
    if (invoiceData.businessAddress) {
      doc.text(invoiceData.businessAddress, 20, yOffsetLeft);
      yOffsetLeft += 5;
    }
    if (invoiceData.phoneNumber) {
      doc.text(`Phone: ${invoiceData.phoneNumber}`, 20, yOffsetLeft);
      yOffsetLeft += 5;
    }
    if (invoiceData.gstNumber) {
      doc.text(`GST: ${invoiceData.gstNumber}`, 20, yOffsetLeft);
      yOffsetLeft += 5;
    }

    // Right box (To)
    doc.setDrawColor(14, 165, 233);
    doc.setFillColor(240, 249, 255);
    doc.roundedRect(110, 58, 85, 35, 2, 2, "FD");
    
    doc.setFontSize(9);
    doc.setTextColor(14, 165, 233);
    doc.setFont("helvetica", "bold");
    doc.text("BILL TO", 115, 63);
    
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.setFont("helvetica", "bold");
    doc.text(invoiceData.customerName || "Customer", 115, 70);
    
    let yOffsetRight = 76;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(71, 85, 105);
    
    if (invoiceData.customerPhone) {
      doc.text(`Phone: ${invoiceData.customerPhone}`, 115, yOffsetRight);
    }

    const tableTop = Math.max(yOffsetLeft, yOffsetRight) + 15;
    
    // Beautiful table header with gradient
    doc.setFillColor(14, 165, 233);
    doc.rect(15, tableTop, 180, 10, "F");
    
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text("PRODUCT CODE", 20, tableTop + 6.5);
    doc.text("DESCRIPTION", 55, tableTop + 6.5);
    doc.text("QTY", 115, tableTop + 6.5, { align: "center" });
    doc.text("PRICE", 145, tableTop + 6.5, { align: "center" });
    doc.text("TOTAL", 175, tableTop + 6.5, { align: "center" });

    let y = tableTop + 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    
    invoiceData.items.forEach((item, index) => {
      // Alternating row colors with subtle gradient effect
      if (index % 2 === 0) {
        doc.setFillColor(240, 249, 255);
      } else {
        doc.setFillColor(255, 255, 255);
      }
      doc.rect(15, y - 5, 180, 9, "F");
      
      // Add subtle border between rows
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.1);
      doc.line(15, y + 4, 195, y + 4);
      
      doc.setTextColor(51, 65, 85);
      doc.text(item.productCode || "-", 20, y);
      
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 41, 59);
      doc.text(item.description || "Item", 55, y);
      
      doc.setFont("helvetica", "normal");
      doc.setTextColor(51, 65, 85);
      doc.text(item.quantity.toString(), 115, y, { align: "center" });
      doc.text(`Rs. ${item.price.toFixed(2)}`, 145, y, { align: "center" });
      
      doc.setFont("helvetica", "bold");
      doc.setTextColor(14, 165, 233);
      doc.text(
        `Rs. ${(item.quantity * item.price).toFixed(2)}`,
        175,
        y,
        { align: "center" }
      );
      y += 9;
    });
    
    // Bottom border of table
    doc.setDrawColor(14, 165, 233);
    doc.setLineWidth(0.5);
    doc.line(15, y - 4, 195, y - 4);

    y += 12;
    
    // Summary box with elegant styling
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(120, y - 5, 75, 45, 2, 2, "F");
    doc.setDrawColor(14, 165, 233);
    doc.setLineWidth(0.3);
    doc.roundedRect(120, y - 5, 75, 45, 2, 2, "D");
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(71, 85, 105);
    doc.text("Subtotal:", 125, y + 2);
    doc.setFont("helvetica", "bold");
    doc.text(`Rs. ${calculateSubtotal().toFixed(2)}`, 188, y + 2, {
      align: "right",
    });
    
    y += 7;
    doc.setFont("helvetica", "normal");
    doc.text("Discount:", 125, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(239, 68, 68);
    doc.text(`- Rs. ${invoiceData.discount.toFixed(2)}`, 188, y, {
      align: "right",
    });
    
    if (invoiceData.taxRate > 0) {
      y += 7;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(71, 85, 105);
      doc.text(`Tax (${invoiceData.taxRate}%)`, 125, y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(14, 165, 233);
      doc.text(`Rs. ${calculateTax().toFixed(2)}`, 188, y, { align: "right" });
    }
    
    // Total section with highlight
    y += 10;
    doc.setFillColor(14, 165, 233);
    doc.rect(120, y - 3, 75, 12, "F");
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("TOTAL AMOUNT:", 125, y + 4);
    doc.setFontSize(13);
    doc.text(`Rs. ${calculateTotal().toFixed(2)}`, 188, y + 4, { align: "right" });

    // Thank you note with style
    y += 25;
    doc.setFillColor(240, 249, 255);
    doc.roundedRect(15, y, 180, 15, 2, 2, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(71, 85, 105);
    doc.text("Thank you for your business!", 105, y + 6, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("We appreciate your trust and look forward to serving you again.", 105, y + 11, { align: "center" });
    
    // Phenomenal footer with Dhandha Karle branding
    doc.setFillColor(14, 165, 233);
    doc.rect(0, 268, 210, 22, "F");
    
    // Orange accent stripe at top of footer
    doc.setFillColor(245, 158, 11);
    doc.rect(0, 268, 210, 3, "F");
    
    // Prominent Dhandha Karle branding box
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(55, 273, 100, 10, 2, 2, "F");
    
    // Main branding text
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(14, 165, 233);
    doc.text("DHANDHA KARLE", 105, 278, { align: "center" });
    
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "normal");
    doc.text("Free Invoice Generator - Grow Your Business", 105, 281.5, { align: "center" });
    
    // Website link
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text("www.dhandhakarle.com", 105, 286, { align: "center" });
    doc.link(70, 284, 70, 4, { url: "https://dhandhakarle.com" });
    
    // Disclaimer at the very bottom
    doc.setFontSize(6);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(255, 255, 255);
    doc.text("Disclaimer: Dhandha Karle is not responsible for the authenticity of invoice generated.", 105, 289, { align: "center" });
    
    return doc;
  };

  const generateTextInvoice = () => {
    let text = `INVOICE\n`;
    text += `Invoice #${invoiceData.invoiceNumber}\n`;
    if (invoiceData.billDate) text += `Invoice Date: ${formatDate(invoiceData.billDate)}\n`;
    text += `Generated by Dhandha Karle\n\n`;

    text += `From:\n`;
    text += `${invoiceData.businessName || "Your Business"}\n`;
    if (invoiceData.businessAddress) text += `${invoiceData.businessAddress}\n`;
    if (invoiceData.phoneNumber) text += `Phone: ${invoiceData.phoneNumber}\n`;
    if (invoiceData.gstNumber) text += `GSTIN: ${invoiceData.gstNumber}\n`;

    text += `\nTo:\n`;
    text += `${invoiceData.customerName || "Customer"}\n`;
    if (invoiceData.billDate) text += `Invoice Date: ${formatDate(invoiceData.billDate)}\n`;
    if (invoiceData.customerPhone)
      text += `Phone: ${invoiceData.customerPhone}\n`;

    text += `\nItems:\n`;
    text += `Product Code    Description          Qty    Price    Total\n`;
    text += `----------------------------------------------------\n`;
    invoiceData.items.forEach((item) => {
      const code = (item.productCode || "").padEnd(15);
      const desc = (item.description || "Item").padEnd(20);
      const qty = item.quantity.toString().padEnd(6);
      const price = `${item.price.toFixed(2)} INR`.padEnd(8);
      const total = `${(item.quantity * item.price).toFixed(2)} INR`;
      text += `${code}${desc}${qty}${price}${total}\n`;
    });

    text += `\n----------------------------------------------------\n`;
    text += `Subtotal: ${calculateSubtotal().toFixed(2)} INR\n`;
    text += `Discount: ${invoiceData.discount.toFixed(2)} INR\n`;
    if (invoiceData.taxRate > 0) {
      text += `Tax (${invoiceData.taxRate}%): ${calculateTax().toFixed(2)} INR\n`;
    }
    text += `Total: ${calculateTotal().toFixed(2)} INR\n`;

    text += `\nPowered by Dhandha Karle - https://dhandhakarle.com\n`;
    text += `*Dhandha Karle is not responsible for invoice accuracy or legal compliance`;

    return text;
  };

  const downloadInvoice = () => {
    if (
      !validateField("businessName", invoiceData.businessName) ||
      !validateField("invoiceNumber", invoiceData.invoiceNumber) ||
      !validateField("billDate", invoiceData.billDate) ||
      !validateField("items")
    )
      return;
    const doc = generatePDF();
    doc.save(`invoice_#${invoiceData.invoiceNumber}.pdf`);
  };

  const shareInvoiceOnWhatsApp = () => {
    if (
      !validateField("businessName", invoiceData.businessName) ||
      !validateField("invoiceNumber", invoiceData.invoiceNumber) ||
      !validateField("billDate", invoiceData.billDate) ||
      !validateField("items")
    )
      return;

    const textContent = generateTextInvoice();
    const whatsappText = encodeURIComponent(textContent);
    window.open(`https://wa.me/?text=${whatsappText}`, "_blank");
  };

  return (
    <div className="app">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta
          name="keywords"
          content="free invoice generator, invoice maker online, GST invoice generator, PDF invoice creator, free billing software, invoice generator India, online invoice maker, business invoice template, restaurant invoice, retail invoice generator, WhatsApp invoice sharing, Dhandha Karle, professional invoice maker, unlimited free invoices, invoice generator with GST, small business invoice tool"
        />
        <meta name="author" content="Dhandha Karle Team" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:image"
          content="https://dhandhakarle.com/invoice-og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <link
          rel="canonical"
          href="https://dhandhakarle.com/invoice-generator"
        />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Dhandha Karle Invoice Generator",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
            "description":
              "Free PDF invoice generator with invoice number and GST support for Indian businesses. Share via WhatsApp or download instantly.",
            "publisher": {
              "@type": "Organization",
              "name": "Dhandha Karle",
              "url": "https://dhandhakarle.com",
            },
          })}
        </script>
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      {/* Navbar from AppContent */}
      <nav className="navbar">
        <div className="navbar-content">
          <h1>Dhandha Karle</h1>
          <button className="hamburger" onClick={toggleNav}>
            <span className={`hamburger-line ${isNavOpen ? "open" : ""}`}></span>
            <span className={`hamburger-line ${isNavOpen ? "open" : ""}`}></span>
            <span className={`hamburger-line ${isNavOpen ? "open" : ""}`}></span>
          </button>
          <div className={`nav-links ${isNavOpen ? "open" : ""}`}>
            {[
              { name: "Home", fn: goToHome },
              { name: "Products", fn: goToHome },
              { name: "Solutions", fn: goToHome },
              { name: "Free Invoice Generator" },
              { name: "Features", fn: goToHome },
              { name: "Pricing", fn: goToHome },
              { name: "Testimonials", fn: goToHome },
              { name: "Support", fn: goToHome },
              { name: "About", fn: goToHome },
              { name: "Contact", fn: toggleContactForm },
            ].map((item) => (
              <button
                key={item.name}
                onClick={item.fn ? item.fn : undefined}
                className={`nav-link ${
                  item.name === "Free Invoice Generator" ? "active" : ""
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Existing InvoiceGenerator Header */}
      <header className="invoice-header">
        <h1>AI-Powered Invoice Generator</h1>
        <p className="subtitle">Smart invoicing with artificial intelligence - Free Forever</p>
      </header>

      {/* AI Assistant Panel */}
      {showAiPanel && (
        <motion.div
          className="ai-panel"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="ai-header">
            <h3>AI Assistant</h3>
            <button onClick={() => setShowAiPanel(false)} className="ai-close">X</button>
          </div>
          
          <div className="ai-features">
            <div className="ai-feature-card">
              <div className="ai-icon-box">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div>
                <h4>Smart Suggestions</h4>
                <p>AI suggests products and prices based on your business</p>
              </div>
            </div>
            
            <div className="ai-feature-card">
              <div className="ai-icon-box">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <path d="M3 9h18"/>
                  <path d="M9 21V9"/>
                </svg>
              </div>
              <div>
                <h4>Auto Tax Calculator</h4>
                <p>Intelligent GST rate recommendations</p>
                <button onClick={applySmartTax} className="ai-action-btn">Apply Smart Tax</button>
              </div>
            </div>
          </div>

          {aiSuggestions.length > 0 && (
            <div className="ai-suggestions">
              <h4>AI Insights for Your Business:</h4>
              {aiSuggestions.map((suggestion, idx) => (
                <div key={idx} className="ai-suggestion-item">
                  + {suggestion}
                </div>
              ))}
            </div>
          )}

          <div className="ai-products">
            <h4>Quick Add Common Products:</h4>
            <div className="ai-product-grid">
              {commonProducts.slice(0, 4).map((product, idx) => (
                <button
                  key={idx}
                  onClick={() => addAiProduct(product)}
                  className="ai-product-btn"
                >
                  <div className="product-name">{product.name}</div>
                  <div className="product-price">Rs. {product.price}</div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Existing InvoiceGenerator Form Section */}
      <section className="invoice-form-section">
        <form className="invoice-form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <h2>Business Details</h2>
            <label htmlFor="invoiceNumber">Invoice Number *</label>
            <input
              type="number"
              name="invoiceNumber"
              value={invoiceData.invoiceNumber}
              onChange={handleInputChange}
              placeholder="e.g., 123"
              min="1"
              className={errors.invoiceNumber ? "error" : ""}
            />
            {errors.invoiceNumber && (
              <span className="error-text">{errors.invoiceNumber}</span>
            )}
            <label htmlFor="billDate">Invoice Date *</label>
            <input
              type="date"
              name="billDate"
              value={invoiceData.billDate}
              onChange={handleInputChange}
              placeholder="Select Date"
              required
              className={errors.billDate ? "error" : ""}
            />
            {errors.billDate && (
              <span className="error-text">{errors.billDate}</span>
            )}
            <label htmlFor="businessName">Business Name *</label>
            <input
              type="text"
              name="businessName"
              value={invoiceData.businessName}
              onChange={handleInputChange}
              placeholder="Business Name"
              className={errors.businessName ? "error" : ""}
            />
            {errors.businessName && (
              <span className="error-text">{errors.businessName}</span>
            )}
            <label htmlFor="businessAddress">Business Address</label>
            <input
              type="text"
              name="businessAddress"
              value={invoiceData.businessAddress}
              onChange={handleInputChange}
              placeholder="Business Address"
            />
            <label htmlFor="phoneNumber">Business Phone</label>
            <input
              type="tel"
              name="phoneNumber"
              value={invoiceData.phoneNumber}
              onChange={handleInputChange}
              placeholder="Business Phone Number"
              maxLength="10"
              className={errors.phoneNumber ? "error" : ""}
            />
            {errors.phoneNumber && (
              <span className="error-text">{errors.phoneNumber}</span>
            )}
            <label htmlFor="gstNumber">GST Number</label>
            <input
              type="text"
              name="gstNumber"
              value={invoiceData.gstNumber}
              onChange={handleInputChange}
              placeholder="GST Number (Optional)"
            />
          </div>

          <div className="form-group">
            <h2>Customer Details</h2>
            <label htmlFor="customerName">Customer Name</label>
            <input
              type="text"
              name="customerName"
              value={invoiceData.customerName}
              onChange={handleInputChange}
              placeholder="Customer Name"
            />
            <label htmlFor="customerPhone">Customer Phone</label>
            <input
              type="tel"
              name="customerPhone"
              value={invoiceData.customerPhone}
              onChange={handleInputChange}
              placeholder="Phone Number"
              maxLength="10"
              className={errors.customerPhone ? "error" : ""}
            />
            {errors.customerPhone && (
              <span className="error-text">{errors.customerPhone}</span>
            )}
          </div>

          <div className="form-group">
            <h2>Items</h2>
            {invoiceData.items.map((item, index) => (
              <div key={index} className="item-row">
                <div className="item-field">
                  <label htmlFor={`productCode-${index}`}>Product Code</label>
                  <input
                    type="text"
                    value={item.productCode}
                    onChange={(e) =>
                      handleItemChange(index, "productCode", e.target.value)
                    }
                    placeholder="Product Code"
                    className="item-productCode"
                  />
                </div>
                <div className="item-field">
                  <label htmlFor={`description-${index}`}>Description *</label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) =>
                      handleItemChange(index, "description", e.target.value)
                    }
                    placeholder="Product Name"
                    className={`item-description ${
                      errors.items && !item.description ? "error" : ""
                    }`}
                  />
                </div>
                <div className="item-field">
                  <label htmlFor={`quantity-${index}`}>Quantity</label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(index, "quantity", e.target.value)
                    }
                    placeholder="e.g., 1"
                    className="item-quantity"
                    min="1"
                  />
                </div>
                <div className="item-field">
                  <label htmlFor={`price-${index}`}>Price</label>
                  <input
                    type="number"
                    value={item.price}
                    onChange={(e) =>
                      handleItemChange(index, "price", e.target.value)
                    }
                    placeholder="Price"
                    className="item-price"
                    min="0.01"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="remove-item-btn"
                >
                  ×
                </button>
              </div>
            ))}
            {errors.items && <span className="error-text">{errors.items}</span>}
            <button type="button" onClick={addItem} className="add-item-btn">
              + Add Item
            </button>
          </div>

          <div className="form-group">
            <h2>Additional Charges</h2>
            <label htmlFor="discount">Discount</label>
            <input
              type="number"
              name="discount"
              value={invoiceData.discount}
              onChange={handleInputChange}
              placeholder="e.g., 50.00"
              min="0"
            />
            <label htmlFor="taxRate">Tax Rate (%)</label>
            <input
              type="number"
              name="taxRate"
              value={invoiceData.taxRate}
              onChange={handleInputChange}
              placeholder="e.g., 10"
              min="0"
            />
          </div>

          <div className="button-group">
            <button
              type="button"
              onClick={downloadInvoice}
              className="action-btn download-btn"
            >
              Download PDF
            </button>
            <button
              type="button"
              onClick={shareInvoiceOnWhatsApp}
              className="action-btn whatsapp-btn"
            >
              Share via WhatsApp
            </button>
          </div>
        </form>
      </section>

      {/* Contact and Demo Forms Section */}
      <section ref={contactRef} className="section forms">
        {showContactForm && (
          <motion.form
            onSubmit={(e) => sendEmail(e, "Contact")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="form-card"
          >
            <h3>Contact Us</h3>
            <input type="text" name="name" placeholder="Your Name" required />
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              required
            />
            <textarea
              name="message"
              placeholder="Your Message"
              required
            ></textarea>
            <button type="submit" className="cta-button">
              Send Message
            </button>
          </motion.form>
        )}
        {showDemoForm && (
          <motion.form
            onSubmit={(e) => sendEmail(e, "Demo")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="form-card"
          >
            <h3>Request a Demo</h3>
            <input type="text" name="name" placeholder="Your Name" required />
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              required
            />
            <input
              type="tel"
              name="phone"
              placeholder="Your Phone Number"
              required
            />
            <textarea
              name="message"
              placeholder="Tell us about your business"
              required
            ></textarea>
            <button type="submit" className="cta-button">
              Schedule Demo
            </button>
          </motion.form>
        )}
      </section>
    </div>
  );
};

export default InvoiceGenerator;